/**
 * Database Fields panel.
 *
 * The left rail is the live PostgreSQL catalog for the chosen primary table,
 * nothing else. Controls live in the top toolbox. Before a primary table is
 * chosen this list is empty by design: there is no implicit or default table.
 * Sensitive columns never reach here — the backend catalog excludes them.
 *
 * What changes here is only how that catalog READS. lib/formDesignerFields.js
 * turns each column into a human-friendly row — `patient_id` is drawn as
 * `Patient Name · Lookup` — while the row still carries the real column name
 * into the drag payload, so binding is untouched. The technical truth stays one
 * hover away, and technical columns are held back behind a toggle rather than
 * removed.
 *
 * The search box narrows what is drawn and nothing else: it never reloads the
 * catalog, never changes the form, and never hides a field from the model.
 */
import { useMemo, useState } from "react";
import {
  Braces,
  Calendar,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Database,
  Hash,
  KeyRound,
  Link2,
  List,
  Search,
  Table2,
  Type,
  User,
} from "lucide-react";
import { FD } from "@/constants/testIds";
import { SELECT_TABLE_MESSAGE, isFieldBound } from "@/lib/formDesigner";
import {
  describeFields,
  fieldTooltip,
  matchesFieldQuery,
  partitionFields,
  tableDisplayName,
} from "@/lib/formDesignerFields";
import {
  FHIR_RESOURCES,
  fhirResourceForTable,
  matchesSemanticQuery,
  semanticControlLabel,
  semanticFieldTooltip,
} from "@/lib/formDesignerSemantic";
import { DRAG_MIME } from "./FormDesignerCanvas";

function setDragPayload(event, payload) {
  event.dataTransfer?.setData?.(DRAG_MIME, JSON.stringify(payload));
  if (event.dataTransfer) event.dataTransfer.effectAllowed = "copy";
}

/** A glyph per presented field kind, so a row is scannable at a glance. */
const GLYPHS = {
  person: User,
  link: Link2,
  choice: List,
  date: Calendar,
  time: Clock,
  number: Hash,
  check: CheckSquare,
  structured: Braces,
  key: KeyRound,
  text: Type,
};

/** Nothing is drawn: say whether it was the search or the technical toggle. */
function emptyMessage(query, technicalCount) {
  const needle = String(query || "").trim();
  if (!needle) return "Every field on this table is technical.";
  if (technicalCount > 0) return `Only technical fields match “${needle}”.`;
  return `No field matches “${needle}”.`;
}

function FieldRow({ presented, form, readOnly, onAddField }) {
  const Icon = GLYPHS[presented.glyph] || Type;
  return (
    <button
      type="button"
      className={`efd__palette-item efd__palette-item--field${
        isFieldBound(form, presented.databaseColumn) ? " is-used" : ""
      }${presented.isTechnical ? " is-technical" : ""}`}
      data-testid={`${FD.paletteField}-${presented.databaseColumn}`}
      data-control-type={presented.controlType}
      data-field-category={presented.category}
      data-technical={presented.isTechnical ? "true" : "false"}
      draggable={!readOnly}
      disabled={readOnly}
      title={fieldTooltip(presented)}
      onDragStart={(event) => setDragPayload(event, { kind: "field", name: presented.databaseColumn })}
      onClick={() => onAddField(presented.field)}
    >
      <Icon className="efd__palette-glyph" aria-hidden="true" />
      <span className="efd__palette-text">
        <span className="efd__palette-name">
          {presented.displayLabel}
          {presented.required ? (
            <span className="efd__palette-required" aria-hidden="true">
              *
            </span>
          ) : null}
        </span>
        <span className="efd__palette-type">{presented.controlLabel}</span>
      </span>
    </button>
  );
}

function isSemanticBound(form, field) {
  return (form?.controls || []).some(
    (control) =>
      control.binding?.id === field.id
      || (field.databaseColumn && control.field === field.databaseColumn),
  );
}

function SemanticRow({ field, form, readOnly, onAddSemantic, depth = 0 }) {
  const [open, setOpen] = useState(field.fieldType === "composite");
  const Icon = field.fieldType === "composite" ? User : GLYPHS[field.fieldType === "reference" ? "link" : field.controlType === "checkbox" ? "check" : field.controlType === "date" ? "date" : "text"] || Type;
  const hasChildren = (field.components || []).length > 0;
  return (
    <div className="efd__semantic-block" data-depth={depth}>
      <button
        type="button"
        className={`efd__palette-item efd__palette-item--field efd__palette-item--semantic${
          isSemanticBound(form, field) ? " is-used" : ""
        }${field.fieldType === "composite" ? " is-composite" : ""}`}
        data-testid={`${FD.paletteSemanticField}-${field.id}`}
        data-fhir-path={field.fhirPath}
        data-field-type={field.fieldType}
        data-resource-type={field.resourceType}
        data-control-type={field.controlType || "text"}
        draggable={!readOnly}
        disabled={readOnly}
        title={semanticFieldTooltip(field)}
        style={{ paddingLeft: `${8 + depth * 12}px` }}
        onDragStart={(event) => setDragPayload(event, { kind: "semantic", id: field.id })}
        onClick={() => onAddSemantic(field)}
      >
        {hasChildren ? (
          <span
            className="efd__semantic-caret"
            onClick={(event) => {
              event.stopPropagation();
              setOpen((current) => !current);
            }}
          >
            {open ? <ChevronDown className="efd__icon" aria-hidden="true" /> : <ChevronRight className="efd__icon" aria-hidden="true" />}
          </span>
        ) : (
          <Icon className="efd__palette-glyph" aria-hidden="true" />
        )}
        <span className="efd__palette-text">
          <span className="efd__palette-name">
            {field.label}
            {field.required ? (
              <span className="efd__palette-required" aria-hidden="true">
                *
              </span>
            ) : null}
          </span>
          <span className="efd__palette-type">{semanticControlLabel(field)}</span>
        </span>
      </button>
      {open && hasChildren
        ? field.components.map((child) => (
            <SemanticRow
              key={child.id}
              field={child}
              form={form}
              readOnly={readOnly}
              onAddSemantic={onAddSemantic}
              depth={depth + 1}
            />
          ))
        : null}
    </div>
  );
}

export default function FormDesignerPalette({
  primaryTable,
  fields,
  status,
  form,
  onAddField,
  onAddSemantic,
  onCollapse,
  readOnly = false,
}) {
  const [query, setQuery] = useState("");
  const [showTechnical, setShowTechnical] = useState(false);
  const [source, setSource] = useState("database");
  const fhirResource = fhirResourceForTable(primaryTable);

  const presented = useMemo(() => describeFields(fields), [fields]);
  const matched = useMemo(
    () => presented.filter((item) => matchesFieldQuery(item, query)),
    [presented, query],
  );
  const { everyday, technical } = useMemo(() => partitionFields(matched), [matched]);
  const drawn = showTechnical ? [...everyday, ...technical] : everyday;

  return (
    <aside className="efd__palette efd__panel" data-testid={FD.palette}>
      <div className="efd__panel-head">
        <h3 className="efd__panel-title">
          <Database className="efd__icon" aria-hidden="true" />
          Database Fields
        </h3>
        <button
          type="button"
          className="efd__icon-btn"
          data-testid={FD.paletteCollapse}
          aria-label="Collapse Database Fields"
          title="Collapse Database Fields"
          onClick={onCollapse}
        >
          <ChevronLeft className="efd__icon" aria-hidden="true" />
        </button>
      </div>

      <div className="efd__search">
        <Search className="efd__search-icon" aria-hidden="true" />
        <input
          className="efd__search-input"
          data-testid={FD.paletteSearch}
          value={query}
          placeholder="Search fields..."
          aria-label="Search fields"
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      <div className="efd__palette-tabs" role="tablist" aria-label="Field source">
        <button
          type="button"
          role="tab"
          className={`efd__palette-tab${source === "fhir" ? " is-active" : ""}`}
          data-testid={FD.paletteFhirTab}
          aria-selected={source === "fhir"}
          onClick={() => setSource("fhir")}
        >
          FHIR Fields
        </button>
        <button
          type="button"
          role="tab"
          className={`efd__palette-tab${source === "database" ? " is-active" : ""}`}
          data-testid={FD.paletteDbTab}
          aria-selected={source === "database"}
          onClick={() => setSource("database")}
        >
          Database Columns
        </button>
      </div>

      {/* The table a form is built on, named the way the user thinks of it.
          The real table name stays one hover away. */}
      {primaryTable ? (
        <div
          className="efd__palette-table"
          data-testid={FD.paletteTable}
          data-table={primaryTable}
          title={`Database table: ${primaryTable}`}
        >
          <Table2 className="efd__icon" aria-hidden="true" />
          <span className="efd__palette-table-text">
            <span className="efd__palette-table-eyebrow">Table</span>
            <span className="efd__palette-table-name">{tableDisplayName(primaryTable)}</span>
          </span>
        </div>
      ) : null}

      {source === "fhir" ? (
        <div className="efd__palette-group" data-testid={FD.paletteSemantic}>
          {!primaryTable ? (
            <p className="efd__palette-empty" data-testid={FD.paletteFieldsEmpty}>
              {SELECT_TABLE_MESSAGE}
            </p>
          ) : null}
          {primaryTable && !fhirResource ? (
            <p className="efd__palette-empty">
              No FHIR resource is mapped to {tableDisplayName(primaryTable)} yet. Patient is the first
              implemented catalog. Use Database Columns for this table.
            </p>
          ) : null}
          {FHIR_RESOURCES.map((resource) => {
            const mapped = resource.table === String(primaryTable || "").toLowerCase();
            const visible = resource.fields.filter((field) => matchesSemanticQuery(field, query));
            if (!mapped && resource.fields.length === 0) {
              return (
                <div
                  key={resource.resourceType}
                  className="efd__semantic-resource is-empty"
                  data-testid={`${FD.paletteSemanticResource}-${resource.resourceType}`}
                >
                  <p className="efd__palette-group-title">{resource.label}</p>
                  <p className="efd__palette-empty">Coming later</p>
                </div>
              );
            }
            if (!mapped) return null;
            return (
              <div
                key={resource.resourceType}
                className="efd__semantic-resource"
                data-testid={`${FD.paletteSemanticResource}-${resource.resourceType}`}
              >
                <p className="efd__palette-group-title">{resource.label}</p>
                {visible.length === 0 ? (
                  <p className="efd__palette-empty">
                    {query.trim() ? `No field matches “${query.trim()}”.` : `${resource.label} has no semantic fields.`}
                  </p>
                ) : (
                  visible.map((field) => (
                    <SemanticRow
                      key={field.id}
                      field={field}
                      form={form}
                      readOnly={readOnly}
                      onAddSemantic={onAddSemantic}
                    />
                  ))
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      {source === "database" ? (
      <>
      <div className="efd__palette-group" data-testid={FD.paletteFields}>
        {!primaryTable ? (
          <p className="efd__palette-empty" data-testid={FD.paletteFieldsEmpty}>
            {SELECT_TABLE_MESSAGE}
          </p>
        ) : null}
        {primaryTable && status === "loading" ? <p className="efd__palette-empty">Loading fields…</p> : null}
        {primaryTable && status === "ready" && presented.length === 0 ? (
          <p className="efd__palette-empty">{tableDisplayName(primaryTable)} has no bindable fields.</p>
        ) : null}
        {primaryTable && presented.length > 0 && drawn.length === 0 ? (
          <p className="efd__palette-empty">{emptyMessage(query, technical.length)}</p>
        ) : null}
        {everyday.map((item) => (
          <FieldRow
            key={item.databaseColumn}
            presented={item}
            form={form}
            readOnly={readOnly}
            onAddField={onAddField}
          />
        ))}
        {showTechnical && technical.length > 0 ? (
          <p className="efd__palette-group-title" data-testid={FD.paletteTechnicalGroup}>
            Technical
          </p>
        ) : null}
        {showTechnical
          ? technical.map((item) => (
              <FieldRow
                key={item.databaseColumn}
                presented={item}
                form={form}
                readOnly={readOnly}
                onAddField={onAddField}
              />
            ))
          : null}
      </div>

      {/* Keys, audit columns and other plumbing are held back, never removed.
          One click brings them back for an administrator who needs them. */}
      {primaryTable && technical.length > 0 ? (
        <label className="efd__toggle efd__palette-toggle" htmlFor="efd-show-technical">
          <input
            id="efd-show-technical"
            type="checkbox"
            data-testid={FD.paletteTechnical}
            checked={showTechnical}
            onChange={(event) => setShowTechnical(event.target.checked)}
          />
          <span>Show technical fields ({technical.length})</span>
        </label>
      ) : null}
      </>
      ) : null}
    </aside>
  );
}
